import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { adminStyles } from '../../styles/adminStyles';
import { usePopup } from '../../contexts/PopupContext';
import { useAdmin } from '../../contexts/AdminContext';
import { getFirestore, collection, query, orderBy, getDocs, deleteDoc, doc, limit } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import * as DocumentPicker from 'expo-document-picker';


const VenueCard = ({ 
  venue, 
  editingVenue, 
  editFormData,
  onStartEditing,
  onApprove,
  onReject,
  onRevertToPending,
  onCancelEdit,
  onSaveVenue,
  onEditField,
  showStatus=true,
  showVotes=false,
  showManualSelector,
  setShowManualSelector
}) => {
  const { showConfirmation } = usePopup();
  const { addManualToMasterDatabase } = useAdmin();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return '#2ecc71';
      case 'rejected':
        return '#e74c3c';
      case 'pending':
        return '#f1c40f';
      default:
        return '#95a5a6';
    }
  };

  const renderEditField = (label, field, multiline = false) => {
    return (
      <View style={{ marginBottom: 15 }}>
        <Text style={{ color: '#fff', marginBottom: 5, fontWeight: '500' }}>{label}:</Text>
        <TextInput
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 5,
            padding: 10,
            color: '#fff',
            fontFamily: 'VastagoGrotesk',
            minHeight: multiline ? 80 : 40,
            textAlignVertical: multiline ? 'top' : 'center',
            ...(multiline && {
              paddingTop: 10,
              lineHeight: 20,
              textAlign: 'left'
            })
          }}
          value={editFormData[field] || ''}
          onChangeText={(text) => onEditField(field, text)}
          multiline={multiline}
          blurOnSubmit={!multiline}
          returnKeyType={multiline ? 'default' : 'done'}
          placeholder={`Enter ${label.toLowerCase()}`}
          placeholderTextColor="rgba(255, 255, 255, 0.4)"
        />
      </View>
    );
  };

  const renderVotes = (label, upVotes, downVotes) => {
    const totalVotes = upVotes + downVotes;
    return (
      <View style={{
        flexDirection: "row",
        gap: 5,
        borderRadius: 5,
      }}>

        
        <Text style={{ width: "60px", color: "white", fontSize: 14 }}>{label}</Text>
        <View style={{ width: "40px", alignItems: "center" }}>
          <Text style={{ color: "#4caf50", fontSize: 12}}>
            👍 {upVotes || 0}
          </Text>
        </View>
        <View style={{ width: "40px", alignItems: "center" }}>
          <Text style={{ color: "#f44336", fontSize: 12 }}>
            👎 {downVotes || 0}
          </Text>
        </View>
        <View style={{ width: "100px", alignItems: "center" }}>
          <Text style={{ color: "white", fontSize: 12 }}>
            {(((upVotes / totalVotes) * 100) || 0).toFixed(1)}% Positive
          </Text>
        </View>
    </View>
  )}

  const renderManualLinksEditor = () => {
    const manualLinks = editFormData.manualsLinks || [];

    const handleAddLink = () => {
      const newLinks = [...manualLinks, { name: '', url: '' }];
      onEditField('manualsLinks', newLinks);
    };



    const handleDeleteLink = (index) => {
      const newLinks = manualLinks.filter((_, i) => i !== index);
      onEditField('manualsLinks', newLinks);
    };

    const handleMoveLink = (index, direction) => {
      if (
        (direction === 'up' && index === 0) || 
        (direction === 'down' && index === manualLinks.length - 1)
      ) {
        return;
      }

      const newLinks = [...manualLinks];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];
      onEditField('manualsLinks', newLinks);
    };

    const handleUpdateLink = (index, field, value) => {
      const newLinks = [...manualLinks];
      newLinks[index] = { ...newLinks[index], [field]: value };
      onEditField('manualsLinks', newLinks);
    };

    const handleAddToDatabase = (manual) => {
      if (!addManualToMasterDatabase) {
        alert('Manual database function not available');
        return;
      }

      if (!manual.name || !manual.url) {
        alert('Please fill in both name and URL before adding to database.');
        return;
      }

      showConfirmation(
        'Add to Master Database?',
        `Would you like to add "${manual.name}" to the master manuals database? This will make it available for all venues that use similar equipment.`,
        async () => {
          await addManualToMasterDatabase({
            name: manual.name,
            url: manual.url,
            ...(manual.software?.name && manual.software?.url ? { 
              software: { 
                name: manual.software.name, 
                url: manual.software.url 
              } 
            } : {}),
            ...(manual.category ? { category: manual.category } : {}),
            ...(manual.manufacturer ? { manufacturer: manual.manufacturer } : {}),
            ...(manual.model ? { model: manual.model } : {}),
            ...(manual.type ? { type: manual.type } : {}),
            ...(manual.keyword ? { keyword: manual.keyword } : {})
          });
        }
      );
    };

    return (
      <View style={{ marginBottom: 20 }}>
        
        {manualLinks.map((link, index) => (
          <View 
            key={index} 
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              padding: 15,
              marginBottom: 10
            }}
          >
          


            <View style={{ flexDirection: 'row', alignItems: "center", gap: 10 }}>
              <View style={{ width: "25%" }}>
                <Text style={{ color: '#fff', marginBottom: 5 }}>Name:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontFamily: 'VastagoGrotesk'
                  }}
                  value={link.name}
                  onChangeText={(text) => handleUpdateLink(index, 'name', text)}
                  placeholder="Enter manual name"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              <View style={{ width: "60%" }}>
                <Text style={{ color: '#fff', marginBottom: 5 }}>URL:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontFamily: 'VastagoGrotesk'
                  }}
                  value={link.url}
                  onChangeText={(text) => handleUpdateLink(index, 'url', text)}
                  placeholder="Enter manual URL"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>
              <View style={{ flexDirection: 'row', height: 50, gap: 12, width: "10%", alignItems: "center", justifyContent: "center" }}>
                <TouchableOpacity
                  style={{
                    opacity: index === 0 ? 0.5 : 1,
                    padding: 8
                  }}
                  onPress={() => handleMoveLink(index, 'up')}
                  disabled={index === 0}
                >
                  <Text style={{ color: '#fff', fontSize: 18 }}>↑</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    opacity: index === manualLinks.length - 1 ? 0.5 : 1,
                    padding: 8
                  }}
                  onPress={() => handleMoveLink(index, 'down')}
                  disabled={index === manualLinks.length - 1}
                >
                  <Text style={{ color: '#fff', fontSize: 18 }}>↓</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    borderRadius: 4,
                    padding: 8
                  }}
                  onPress={() => handleDeleteLink(index)}
                >
                  <Text style={{ color: '#fff' }}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
        <TouchableOpacity
          style={{
            backgroundColor: '#2980b9',
            padding: 10,
            borderRadius: 5,
            alignItems: 'center',
              flex: 1
          }}
          onPress={handleAddLink}
        >
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>ADD NEW MANUAL</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={{
              backgroundColor: '#27ae60',
              padding: 10,
              borderRadius: 5,
              alignItems: 'center',
              flex: 1
            }}
            onPress={() => setShowManualSelector(true)}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>SELECT EXISTING MANUAL</Text>
        </TouchableOpacity>
        </View>
      </View>
    );
  };

  const [uploadingSpecIndex, setUploadingSpecIndex] = useState(null);

  const renderSpecSheetsEditor = () => {
    const specSheets = editFormData.specSheets || [];

    const handleAddSpecSheet = () => {
      const newSpecSheets = [...specSheets, { name: '', url: '', specType: 'url' }];
      onEditField('specSheets', newSpecSheets);
    };

    const handleDeleteSpecSheet = async (index) => {
      const specToDelete = specSheets[index];
      
      // If it's a file type, delete it from storage
      if (specToDelete?.specType === 'file' && specToDelete?.url) {
        try {
          // Extract the file path from the download URL
          // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
          const url = new URL(specToDelete.url);
          const pathMatch = url.pathname.match(/\/o\/(.+)/);
          
          if (pathMatch) {
            const encodedPath = pathMatch[1];
            const filePath = decodeURIComponent(encodedPath);
            
            const storage = getStorage();
            const fileRef = storageRef(storage, filePath);
            await deleteObject(fileRef);
            console.log('Deleted file from storage:', filePath);
          }
        } catch (error) {
          console.error('Error deleting file from storage:', error);
          // Continue with deletion even if storage deletion fails
        }
      }
      
      const newSpecSheets = specSheets.filter((_, i) => i !== index);
      onEditField('specSheets', newSpecSheets);
    };

    const handleMoveSpecSheet = (index, direction) => {
      if (
        (direction === 'up' && index === 0) || 
        (direction === 'down' && index === specSheets.length - 1)
      ) {
        return;
      }

      const newSpecSheets = [...specSheets];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      [newSpecSheets[index], newSpecSheets[newIndex]] = [newSpecSheets[newIndex], newSpecSheets[index]];
      onEditField('specSheets', newSpecSheets);
    };

    const handleUpdateSpecSheet = (index, field, value) => {
      const newSpecSheets = [...specSheets];
      newSpecSheets[index] = { ...newSpecSheets[index], [field]: value };
      onEditField('specSheets', newSpecSheets);
    };

    const handleUploadFile = async (index) => {
      try {
        setUploadingSpecIndex(index);
        console.log('Opening document picker for spec sheet...');
        const result = await DocumentPicker.getDocumentAsync({ 
          type: '*/*' // Accept any file type
        });
        
        if (!result.canceled && result.assets && result.assets.length > 0) {
          const file = result.assets[0];
          const fileUri = file.uri;
          console.log('File URI:', fileUri);

          // Delete old file if it exists and is a file type
          const oldSpec = specSheets[index];
          if (oldSpec?.specType === 'file' && oldSpec?.url) {
            try {
              const oldUrl = new URL(oldSpec.url);
              const pathMatch = oldUrl.pathname.match(/\/o\/(.+)/);
              if (pathMatch) {
                const encodedPath = pathMatch[1];
                const oldFilePath = decodeURIComponent(encodedPath);
                const storage = getStorage();
                const oldFileRef = storageRef(storage, oldFilePath);
                await deleteObject(oldFileRef);
                console.log('Deleted old file from storage:', oldFilePath);
              }
            } catch (deleteError) {
              console.error('Error deleting old file from storage:', deleteError);
              // Continue with upload even if old file deletion fails
            }
          }

          const response = await fetch(fileUri);
          const fileBlob = await response.blob();
          console.log('File blob created:', fileBlob);

          const storage = getStorage();
          const fileExt = file.name.split('.').pop();
          const fileName = `specSheetMedia/${venue.id}/${Date.now()}_${file.name}`;
          const storageRefPath = storageRef(storage, fileName);

          await uploadBytes(storageRefPath, fileBlob);
          console.log('File uploaded to storage:', storageRefPath.fullPath);

          const downloadURL = await getDownloadURL(storageRefPath);
          console.log('Download URL obtained:', downloadURL);

          // Update the spec sheet with the file URL and type
          const newSpecSheets = [...specSheets];
          newSpecSheets[index] = {
            ...newSpecSheets[index],
            url: downloadURL,
            specType: 'file',
            fileName: file.name,
            fileSize: file.size
          };
          onEditField('specSheets', newSpecSheets);
          
          alert('File uploaded successfully!');
        }
      } catch (error) {
        console.error('Error uploading spec sheet file:', error);
        alert('Error uploading file: ' + error.message);
      } finally {
        setUploadingSpecIndex(null);
      }
    };

    return (
      <View style={{ marginBottom: 20 }}>
        {specSheets.map((spec, index) => (
          <View 
            key={index} 
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              padding: 15,
              marginBottom: 10
            }}
          >
            <View style={{ marginBottom: 10 }}>
              <Text style={{ color: '#fff', marginBottom: 5 }}>Type:</Text>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 5,
                    backgroundColor: spec.specType === 'url' ? '#2980b9' : 'rgba(255, 255, 255, 0.1)',
                    alignItems: 'center'
                  }}
                  onPress={() => handleUpdateSpecSheet(index, 'specType', 'url')}
                >
                  <Text style={{ color: '#fff', fontWeight: 'bold' }}>URL</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 5,
                    backgroundColor: spec.specType === 'file' ? '#2980b9' : 'rgba(255, 255, 255, 0.1)',
                    alignItems: 'center'
                  }}
                  onPress={() => handleUpdateSpecSheet(index, 'specType', 'file')}
                >
                  <Text style={{ color: '#fff', fontWeight: 'bold' }}>FILE</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={{ flexDirection: 'row', alignItems: "center", gap: 10, marginBottom: 10 }}>
              <View style={{ width: "25%" }}>
                <Text style={{ color: '#fff', marginBottom: 5 }}>Name:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontFamily: 'VastagoGrotesk'
                  }}
                  value={spec.name}
                  onChangeText={(text) => handleUpdateSpecSheet(index, 'name', text)}
                  placeholder="Enter spec sheet name"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              {spec.specType === 'url' ? (
                <View style={{ width: "60%" }}>
                  <Text style={{ color: '#fff', marginBottom: 5 }}>URL:</Text>
                  <TextInput
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 5,
                      padding: 10,
                      color: '#fff',
                      fontFamily: 'VastagoGrotesk'
                    }}
                    value={spec.url}
                    onChangeText={(text) => handleUpdateSpecSheet(index, 'url', text)}
                    placeholder="Enter spec sheet URL"
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                  />
                </View>
              ) : (
                <View style={{ width: "60%" }}>
                  <Text style={{ color: '#fff', marginBottom: 5 }}>File:</Text>
                  {spec.url ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Text style={{ color: '#4caf50', flex: 1 }} numberOfLines={1}>
                        {spec.fileName || 'File uploaded'}
                      </Text>
                      <TouchableOpacity
                        style={{
                          backgroundColor: '#27ae60',
                          padding: 8,
                          borderRadius: 5
                        }}
                        onPress={() => handleUploadFile(index)}
                      >
                        <Text style={{ color: '#fff', fontSize: 12 }}>CHANGE</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={{
                        backgroundColor: '#27ae60',
                        padding: 10,
                        borderRadius: 5,
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: 5
                      }}
                      onPress={() => handleUploadFile(index)}
                      disabled={uploadingSpecIndex === index}
                    >
                      {uploadingSpecIndex === index ? (
                        <>
                          <ActivityIndicator size="small" color="#fff" />
                          <Text style={{ color: '#fff', fontWeight: 'bold' }}>UPLOADING...</Text>
                        </>
                      ) : (
                        <Text style={{ color: '#fff', fontWeight: 'bold' }}>UPLOAD FILE</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              )}

              <View style={{ flexDirection: 'row', height: 50, gap: 12, width: "10%", alignItems: "center", justifyContent: "center" }}>
                <TouchableOpacity
                  style={{
                    opacity: index === 0 ? 0.5 : 1,
                    padding: 8
                  }}
                  onPress={() => handleMoveSpecSheet(index, 'up')}
                  disabled={index === 0}
                >
                  <Text style={{ color: '#fff', fontSize: 18 }}>↑</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    opacity: index === specSheets.length - 1 ? 0.5 : 1,
                    padding: 8
                  }}
                  onPress={() => handleMoveSpecSheet(index, 'down')}
                  disabled={index === specSheets.length - 1}
                >
                  <Text style={{ color: '#fff', fontSize: 18 }}>↓</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    borderRadius: 4,
                    padding: 8
                  }}
                  onPress={() => handleDeleteSpecSheet(index)}
                >
                  <Text style={{ color: '#fff' }}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        <TouchableOpacity
          style={{
            backgroundColor: '#2980b9',
            padding: 10,
            borderRadius: 5,
            alignItems: 'center',
            marginTop: 10
          }}
          onPress={handleAddSpecSheet}
        >
          <Text style={{ color: '#fff', fontWeight: 'bold' }}>ADD NEW SPEC SHEET</Text>
        </TouchableOpacity>
      </View>
    );
  };

  if (editingVenue && editingVenue.id === venue.id) {
    return (
      <View key={venue.id} style={[adminStyles.reportCard, { padding: 20 }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 }}>
          <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>Editing Venue</Text>
          {showStatus && (
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(venue.status) }]}>
              {venue.status?.toUpperCase() || 'PENDING'}
            </Text>
          )}
        </View>

        <ScrollView style={{ maxHeight: 500 }}>
          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 10 }}>
              Basic Information
            </Text>
            {renderEditField('Name', 'name')}
            {renderEditField('Address', 'address')}
            {renderEditField('City', 'city')}
            {renderEditField('State', 'state')}
            {renderEditField('Capacity', 'capacity')}
            {renderEditField('ADA Accessibility for Artist', 'adaAccessibilityArtist', true)}
            {renderEditField('Website', 'website')}
          </View>

          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 10 }}>
              Technical Specifications
            </Text>
            {renderEditField('Stage', 'stage', true)}
            {renderEditField('Power', 'power', true)}
            {renderEditField('Lighting Control', 'lightingControl', true)}
            {renderEditField('Lighting Fixtures', 'lightingFixtures', true)}
            {renderEditField('Video', 'video', true)}
            {renderEditField('FOH', 'foh', true)}
            {renderEditField('PA System', 'pa', true)}
            {renderEditField('Monitors', 'monitors', true)}
          </View>

          <View style={{ marginBottom: 20 }}>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 10 }}>
              Additional Information
            </Text>
            {renderEditField('Affiliate', 'affiliate')}
            {renderEditField('Backline', 'backline', true)}
            {renderEditField('Backstage', 'backstage', true)}
            {renderEditField('Merchandise', 'merch', true)}
            {renderEditField('Parking', 'parking', true)}
            {renderEditField('Shore Power', 'shorePower', true)}
            {/* {renderEditField('Spec Sheet Link', 'specSheetLink')} */}
            {renderEditField('Map Embed Link', 'mapEmbedLink')}
            {renderEditField('Notes', 'notes', true)}
          </View>

            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 10 }}>
                Spec Sheets
              </Text>
              {renderSpecSheetsEditor()}
            </View>


            <View style={{ marginBottom: 20 }}>
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 10 }}>
                Manuals Links
              </Text>
              {renderManualLinksEditor()}
            </View>

        </ScrollView>

        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 20, gap: 10 }}>
          <TouchableOpacity
            style={{
              backgroundColor: '#95a5a6',
              padding: 10,
              borderRadius: 5,
              minWidth: 100,
              alignItems: 'center'
            }}
            onPress={onCancelEdit}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>CANCEL</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={{
              backgroundColor: '#2980b9',
              padding: 10,
              borderRadius: 5,
              minWidth: 100,
              alignItems: 'center'
            }}
            onPress={onSaveVenue}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>SAVE</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={[adminStyles.reportCard, { borderLeftWidth: 5, borderLeftColor: getStatusColor(showStatus ? venue.status : null) }]}>
      <View style={[adminStyles.reportHeader, { borderBottomWidth: 1, borderBottomColor: 'rgba(255, 255, 255, 0.1)', paddingBottom: 10 }]}>
        <View style={{ flex: 1 }}>
          <Text style={[adminStyles.venueName, { fontSize: 22 }]}>{venue.name}</Text>
          <Text style={{ color: '#aaa', fontSize: 14 }}>{venue.city}, {venue.state}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          {showStatus && (
            <Text style={[adminStyles.statusBadge, { backgroundColor: getStatusColor(venue.status) }]}>
              {venue.status?.toUpperCase() || 'PENDING'}
            </Text>
          )}
          <TouchableOpacity
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              width: 34,
              height: 34,
              borderRadius: 17,
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onPress={() => onStartEditing(venue)}
          >
            <Text style={{ color: '#fff', fontSize: 16 }}>✎</Text>
          </TouchableOpacity>
        </View>
      </View>

      {showVotes && (
        <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
          <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#fff', marginBottom: 10 }}>
            VOTES
          </Text>
          <View
            style={{
              flexDirection: "column",
              justifyContent: "center",
              width: "100%",
              gap: 10,
              padding: 20,
            }}
          >
            {renderVotes("Overall", venue.upVotes, venue.downVotes)}
            {/* {renderVotes("Title", venue.titleUpVotes, venue.titleDownVotes)} */}
            {renderVotes("Specs", venue.specsUpVotes, venue.specsDownVotes)}
            {renderVotes("Links", venue.linksUpVotes, venue.linksDownVotes)}
          </View>
        </View>
      )}

      <View style={{ flexDirection: 'row', marginTop: 15, marginBottom: 15 }}>
        <View style={{ flex: 1, borderRightWidth: 1, borderRightColor: 'rgba(255, 255, 255, 0.1)', paddingRight: 10 }}>
          <Text style={{ color: '#aaa', fontSize: 12, marginBottom: 5 }}>SUBMITTED BY</Text>
          <Text style={{ color: '#fff', fontSize: 14 }}>
            {venue.submittedBy?.firstName 
              ? `${venue.submittedBy.firstName} ${venue.submittedBy.lastName}`
              : venue.submittedBy?.email || 'Anonymous'}
          </Text>
        </View>
        <View style={{ flex: 1, paddingLeft: 10 }}>
          <Text style={{ color: '#aaa', fontSize: 12, marginBottom: 5 }}>SUBMISSION DATE</Text>
          <Text style={{ color: '#fff', fontSize: 14 }}>
            {venue.createdAt?.toDate ? venue.createdAt.toDate().toLocaleDateString() : 'Unknown'}
          </Text>
        </View>
      </View>

      <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>Basic Information</Text>
          <TouchableOpacity
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              padding: 5,
              borderRadius: 4,
              alignItems: 'center',
            }}
            onPress={() => onStartEditing(venue)}
          >
            <Text style={{ color: '#fff', fontSize: 12 }}>EDIT</Text>
          </TouchableOpacity>
        </View>
        
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>ADDRESS</Text>
            <Text style={{ color: '#fff' }}>{venue.address || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>LOCATION</Text>
            <Text style={{ color: '#fff' }}>{[venue.city, venue.state].filter(Boolean).join(', ') || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>CAPACITY</Text>
            <Text style={{ color: '#fff' }}>{venue.capacity || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>WEBSITE</Text>
            <Text style={{ color: '#fff' }}>{venue.website || 'Not provided'}</Text>
          </View>
        </View>
      </View>
      
      <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
        <Text style={{ color: '#fff', fontWeight: 'bold', marginBottom: 10, fontSize: 16 }}>Technical Specifications</Text>
        
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>STAGE</Text>
            <Text style={{ color: '#fff' }}>{venue.stage || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>POWER</Text>
            <Text style={{ color: '#fff' }}>{venue.power || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>LIGHTING CONTROL</Text>
            <Text style={{ color: '#fff' }}>{venue.lightingControl || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>LIGHTING FIXTURES</Text>
            <Text style={{ color: '#fff' }}>{venue.lightingFixtures || 'Not provided'}</Text>
          </View>

          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>VIDEO</Text>
            <Text style={{ color: '#fff' }}>{venue.video || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>FOH</Text>
            <Text style={{ color: '#fff' }}>{venue.foh || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>PA SYSTEM</Text>
            <Text style={{ color: '#fff' }}>{venue.pa || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>MONITORS</Text>
            <Text style={{ color: '#fff' }}>{venue.monitors || 'Not provided'}</Text>
          </View>
        </View>
      </View>
      
      <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
        <Text style={{ color: '#fff', fontWeight: 'bold', marginBottom: 10, fontSize: 16 }}>Additional Information</Text>
        
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>AFFILIATE</Text>
            <Text style={{ color: '#fff' }}>{venue.affiliate || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>BACKLINE</Text>
            <Text style={{ color: '#fff' }}>{venue.backline || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>BACKSTAGE</Text>
            <Text style={{ color: '#fff' }}>{venue.backstage || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>MERCHANDISE</Text>
            <Text style={{ color: '#fff' }}>{venue.merch || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10, paddingRight: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>PARKING</Text>
            <Text style={{ color: '#fff' }}>{venue.parking || 'Not provided'}</Text>
          </View>
          
          <View style={{ width: '50%', marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>SHORE POWER</Text>
            <Text style={{ color: '#fff' }}>{venue.shorePower || 'Not provided'}</Text>
          </View>
        </View>
        
        <View style={{ marginTop: 5 }}>
          <View style={{ marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>SPEC SHEET LINK</Text>
            <Text style={{ color: '#fff' }}>{venue.specSheetLink || 'Not provided'}</Text>
          </View>
          
          <View style={{ marginBottom: 10 }}>
            <Text style={{ color: '#aaa', fontSize: 12 }}>MAP EMBED LINK</Text>
            <Text style={{ color: '#fff' }}>{venue.mapEmbedLink || 'Not provided'}</Text>
          </View>
        </View>
      </View>
      
      {venue.notes && (
        <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
          <Text style={{ color: '#fff', fontWeight: 'bold', marginBottom: 10, fontSize: 16 }}>Notes</Text>
          <Text style={{ color: '#fff' }}>{venue.notes}</Text>
        </View>
      )}

      {venue.specSheets && venue.specSheets.length > 0 && (
        <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
          <Text style={{ color: '#fff', fontWeight: 'bold', marginBottom: 10, fontSize: 16 }}>Spec Sheets</Text>
          {venue.specSheets.map((spec, index) => (
            <View key={index} style={{ padding: 5, backgroundColor: 'rgba(0, 0, 0, 0.1)', borderRadius: 8, marginBottom: 8 }}>
              <Text style={{ color: '#fff' }}>
                <Text style={{ fontWeight: 'bold' }}>{spec.name || 'Spec Sheet'}</Text>
                {spec.specType === 'file' && (
                  <Text style={{ color: '#3498db', fontSize: 12 }}> (File: {spec.fileName || 'Uploaded file'})</Text>
                )}
                {spec.specType === 'url' && (
                  <Text> - <Text style={{ color: '#00A575' }}>{spec.url}</Text></Text>
                )}
              </Text>
            </View>
          ))}
        </View>
      )}


      {venue.manualsLinks && (
        <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 5, marginBottom: 15, padding: 15 }}>
          <Text style={{ color: '#fff', fontWeight: 'bold', marginBottom: 10, fontSize: 16 }}>Manuals Links</Text>
          {venue.manualsLinks.map((link, index) => (
            <Text key={index} style={{ padding: 5, backgroundColor: 'rgba(0, 0, 0, 0.1)', borderRadius: 8, marginBottom: 8, color: '#fff' }}>
              <Text style={{ fontWeight: 'bold' }}>{link.name}</Text> - <Text style={{ color: '#00A575' }}>{link.url}</Text>
            </Text>
          ))}
        </View>
      )}

      {showStatus && (
        <View style={[adminStyles.actionButtonsContainer, { justifyContent: 'flex-end' }]}>
            {venue.status === 'pending' ? (
            <>
                <TouchableOpacity
                style={[adminStyles.actionButton, { backgroundColor: '#2ecc71' }]}
                onPress={() => onApprove(venue)}
                >
                <Text style={adminStyles.actionButtonText}>APPROVE</Text>
                </TouchableOpacity>
                <TouchableOpacity
                style={[adminStyles.actionButton, { backgroundColor: '#e74c3c' }]}
                onPress={() => onReject(venue)}
                >
                <Text style={adminStyles.actionButtonText}>DENY</Text>
                </TouchableOpacity>
            </>
            ) : (
            <TouchableOpacity
                style={[adminStyles.actionButton, { backgroundColor: '#f1c40f' }]}
                onPress={() => onRevertToPending(venue)}
            >
                <Text style={adminStyles.actionButtonText}>REVERT TO PENDING</Text>
            </TouchableOpacity>
            )}
        </View>
      )}

      {/* Discussion Board Section for Admin */}
      <VenueDiscussionBoard venueId={venue.id} venueName={venue.name} />

    </View>
  );
};

// Venue Discussion Board Component for Admin
const VenueDiscussionBoard = ({ venueId, venueName }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const q = query(
        collection(db, `venues/${venueId}/discussions`),
        orderBy('timestamp', 'desc'),
        limit(50) // Load more messages for admin
      );

      const querySnapshot = await getDocs(q);
      const messageData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messageData.push({ 
          id: doc.id, 
          ...data,
          // Properly handle timestamp conversion
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : 
                    data.timestamp?.seconds ? new Date(data.timestamp.seconds * 1000) :
                    new Date(data.timestamp) || new Date(),
          // Ensure text content is available
          text: data.text || data.message || data.content || '',
          // Ensure username is available
          username: data.username || data.user || data.displayName || 'Anonymous'
        });
      });

      setMessages(messageData);
      setMessageCount(messageData.length); // Update count after loading
    } catch (error) {
      console.error('Error loading discussion messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, `venues/${venueId}/discussions`, messageId));
      
      // Remove from local state
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setMessageCount(prev => prev - 1); // Update count after deletion
      alert('Message deleted successfully');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  useEffect(() => {
    if (isExpanded) {
      loadMessages();
    }
  }, [isExpanded, venueId]);

  // Load initial message count when component mounts
  useEffect(() => {
    const loadMessageCount = async () => {
      try {
        const db = getFirestore();
        const q = query(collection(db, `venues/${venueId}/discussions`));
        const querySnapshot = await getDocs(q);
        setMessageCount(querySnapshot.size);
      } catch (error) {
        console.error('Error loading message count:', error);
        setMessageCount(0);
      }
    };
    
    loadMessageCount();
  }, [venueId]);

  // Auto-refresh messages every 30 seconds when expanded
  useEffect(() => {
    let interval;
    if (isExpanded) {
      interval = setInterval(() => {
        loadMessages();
      }, 30000); // 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isExpanded]);

  return (
    <View style={adminStyles.discussionBoardSection}>
      <TouchableOpacity
        style={adminStyles.discussionToggle}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <Text style={adminStyles.discussionToggleText}>
          Discussion Board ({isExpanded ? messages.length : messageCount} messages) {isExpanded ? '▼' : '▶'}
        </Text>
      </TouchableOpacity>

      {isExpanded && (
        <View style={adminStyles.discussionContent}>
          <View style={adminStyles.discussionHeader}>
            <Text style={adminStyles.discussionTitle}>Community Messages for {venueName}</Text>
            <TouchableOpacity
              style={adminStyles.refreshButton}
              onPress={loadMessages}
            >
              <Text style={adminStyles.refreshButtonText}>Refresh</Text>
            </TouchableOpacity>
          </View>

          {loading ? (
            <Text style={adminStyles.loadingText}>Loading messages...</Text>
          ) : messages.length === 0 ? (
            <Text style={adminStyles.noMessagesText}>No messages found</Text>
          ) : (
            <ScrollView style={adminStyles.messagesScrollView} nestedScrollEnabled>
              {messages.map((message) => (
                <View key={message.id} style={adminStyles.adminMessageCard}>
                  <View style={adminStyles.adminMessageHeader}>
                    <View style={adminStyles.adminMessageInfo}>
                      <Text style={adminStyles.adminMessageUser}>
                        {message.username || 'Anonymous'}
                      </Text>
                      <Text style={adminStyles.adminMessageTime}>
                        {message.timestamp instanceof Date ? 
                          message.timestamp.toLocaleString() : 
                          'Unknown time'}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={adminStyles.adminDeleteButton}
                      onPress={() => {
                        if (confirm('Are you sure you want to delete this message?')) {
                          deleteMessage(message.id);
                        }
                      }}
                    >
                      <Text style={adminStyles.adminDeleteButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={adminStyles.adminMessageText}>
                    {message.text || 'No text content'}
                  </Text>
                  {message.photoURL && (
                    <img 
                      src={message.photoURL} 
                      alt="Message attachment" 
                      style={adminStyles.adminMessageImage}
                    />
                  )}
                </View>
              ))}
            </ScrollView>
          )}
        </View>
      )}
    </View>
  );
};

// Manual Selector Modal Component - Rendered as a separate overlay
const ManualSelectorModal = ({ 
  showManualSelector, 
  setShowManualSelector, 
  manualSearchQuery, 
  setManualSearchQuery,
  availableManuals,
  loadingManuals,
  onSelectManual
}) => {
  if (!showManualSelector) return null;

  const filteredManuals = availableManuals.filter(manual => {
    const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
    const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
    return nameMatch || keywordMatch;
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      zIndex: 99999,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backdropFilter: 'blur(5px)'
    }}>
      <div style={{
        backgroundColor: '#2c3e50',
        borderRadius: '10px',
        padding: '20px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '85vh',
        border: '2px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
            Select Equipment Manual
          </h3>
          <button
            style={{ 
              padding: '8px',
              borderRadius: '15px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
            onClick={() => {
              setShowManualSelector(false);
              setManualSearchQuery('');
            }}
          >
            ✕
          </button>
        </div>

        <input
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '12px',
            color: '#fff',
            marginBottom: '15px',
            fontSize: '14px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            width: '100%',
            boxSizing: 'border-box'
          }}
          placeholder="Search manuals by name or keyword..."
          value={manualSearchQuery}
          onChange={(e) => setManualSearchQuery(e.target.value)}
        />

        <p style={{ 
          color: 'rgba(255, 255, 255, 0.7)', 
          fontSize: '12px', 
          marginBottom: '10px',
          margin: '0 0 10px 0'
        }}>
          {loadingManuals ? 'Loading...' : `${filteredManuals.length} of ${availableManuals.length} manuals`}
        </p>

        <div style={{ 
          maxHeight: '350px',
          marginBottom: '15px',
          overflowY: 'auto'
        }}>
          {loadingManuals ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <p style={{ color: '#fff', fontSize: '16px', margin: 0 }}>
                Loading manuals...
              </p>
            </div>
          ) : filteredManuals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px' }}>
              <p style={{ color: '#fff', fontSize: '16px', textAlign: 'center', margin: 0 }}>
                {manualSearchQuery ? 'No manuals found matching your search.' : 'No manuals available.'}
              </p>
              {manualSearchQuery && (
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', textAlign: 'center', marginTop: '5px' }}>
                  Try searching for different keywords or equipment names.
                </p>
              )}
            </div>
          ) : (
            filteredManuals.map((manual, index) => (
              <div
                key={manual.keyword}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => onSelectManual(manual)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }}
              >
                <p style={{ 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  marginBottom: '5px',
                  fontSize: '14px',
                  margin: '0 0 5px 0'
                }}>
                  {manual.name}
                </p>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  fontSize: '11px',
                  marginBottom: '3px',
                  margin: '0 0 3px 0'
                }}>
                  Keyword: {manual.keyword} • URL: {manual.url.length > 50 ? manual.url.substring(0, 50) + '...' : manual.url}
                </p>
                {manual.software?.name && (
                  <p style={{ 
                    color: 'rgba(52, 152, 219, 0.9)', 
                    fontSize: '11px',
                    marginBottom: '3px',
                    margin: '0 0 3px 0'
                  }}>
                    📦 Software: {manual.software.name}
                  </p>
                )}
                {manual.category && (
                  <p style={{ 
                    color: 'rgba(46, 204, 113, 0.9)', 
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    margin: '0'
                  }}>
                    {manual.category}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            style={{
              backgroundColor: '#95a5a6',
              padding: '12px 30px',
              borderRadius: '8px',
              minWidth: '100px',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
            onClick={() => {
              setShowManualSelector(false);
              setManualSearchQuery('');
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced VenueCard with Modal
const VenueCardWithModal = (props) => {
  const [showManualSelector, setShowManualSelector] = React.useState(false);
  const [manualSearchQuery, setManualSearchQuery] = React.useState('');
  const [availableManuals, setAvailableManuals] = React.useState([]);
  const [loadingManuals, setLoadingManuals] = React.useState(false);

  // Load available manuals from Firebase
  const loadAvailableManuals = async () => {
    try {
      setLoadingManuals(true);
      const { getFirestore, collection, getDocs } = await import('firebase/firestore');
      const db = getFirestore();
      const manualsSnapshot = await getDocs(collection(db, 'equipment_manuals'));
      
      const manuals = [];
      manualsSnapshot.forEach((doc) => {
        manuals.push({
          keyword: doc.id,
          ...doc.data()
        });
      });
      
      setAvailableManuals(manuals);
    } catch (error) {
      console.error('Error loading available manuals:', error);
      // Fallback to static data
      try {
        const { EQUIPMENT_MANUALS } = require('../../utils/findRelevantManuals');
        const manuals = Object.entries(EQUIPMENT_MANUALS).map(([keyword, manual]) => ({
          keyword,
          name: manual.name,
          url: manual.url,
          software: manual.software
        }));
        setAvailableManuals(manuals);
      } catch (fallbackError) {
        console.error('Error loading fallback manuals:', fallbackError);
        setAvailableManuals([]);
      }
    } finally {
      setLoadingManuals(false);
    }
  };

  // Load manuals when selector opens
  React.useEffect(() => {
    if (showManualSelector && availableManuals.length === 0) {
      loadAvailableManuals();
    }
  }, [showManualSelector]);

  const handleSelectManual = (manual) => {
    const manualEntry = {
      name: manual.name,
      url: manual.url,
      ...(manual.software?.name && manual.software?.url ? { 
        software: { 
          name: manual.software.name, 
          url: manual.software.url 
        } 
      } : {}),
      ...(manual.category ? { category: manual.category } : {}),
      ...(manual.manufacturer ? { manufacturer: manual.manufacturer } : {}),
      ...(manual.model ? { model: manual.model } : {}),
      ...(manual.type ? { type: manual.type } : {}),
      keyword: manual.keyword
    };
    
    const manualLinks = props.editFormData.manualsLinks || [];
    const newLinks = [...manualLinks, manualEntry];
    props.onEditField('manualsLinks', newLinks);
    setShowManualSelector(false);
    setManualSearchQuery('');
  };

  return (
    <>
      <VenueCard 
        {...props} 
        showManualSelector={showManualSelector}
        setShowManualSelector={setShowManualSelector}
      />
      <ManualSelectorModal
        showManualSelector={showManualSelector}
        setShowManualSelector={setShowManualSelector}
        manualSearchQuery={manualSearchQuery}
        setManualSearchQuery={setManualSearchQuery}
        availableManuals={availableManuals}
        loadingManuals={loadingManuals}
        onSelectManual={handleSelectManual}
      />
    </>
  );
};

export default VenueCardWithModal;